import React, { useState } from "react";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import {
  CircularProgress,
  IconButton,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { addDoc, collection, deleteDoc, doc } from "firebase/firestore";
import { db } from "firebaseApp";
import { colors } from "colors";
import { useDispatch } from "react-redux";
import { chatActions } from "store/chat";
import ConfirmModal from "components/react-lib/generic-ui/ConfirmModal";
import DeleteIcon from "@mui/icons-material/Delete";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import Markdown from "react-markdown";
import ReactDOMServer from "react-dom/server";
import CopyAllIcon from "@mui/icons-material/CopyAll";
import { COLLECTIONS } from "firebaseCollections";
import { popupActions } from "store/react-lib/generic-ui/popup";
import ShareIcon from "@mui/icons-material/Share";
import { getShareUrl } from "utils/share";

export default function TalkMessageMenu({ message }) {

  const dispatch = useDispatch();

  const [anchorEl, setAnchorEl] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const [sharing, setSharing] = useState(false);

  const [detailsOpen, setDetailsOpen] = useState(false);

  const open = Boolean(anchorEl);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleDelete = async () => {
    try {
      setProcessing(true);
      const messageRef = doc(db, "talks", message.talkId, "messages", message.id);
      await deleteDoc(messageRef);
      handleClose();
    } catch (error) {
      console.log(error);
    } finally {
      setProcessing(false);
    }
  };

  const handleCopy = async () => {
    await navigator.clipboard.writeText(message.content);
    dispatch(popupActions.showSuccess("Copied raw message"));
    handleClose();
  };

  const copyMarkdownToClipboard = async () => {
    const markdown = message.content;
    try {
      const htmlString = ReactDOMServer.renderToString(
        <Markdown>{markdown}</Markdown>
      );
      if (navigator.clipboard && window.ClipboardItem && window.Blob) {
        await navigator.clipboard.write([
          new ClipboardItem({
            "text/html": new Blob([htmlString], { type: "text/html" }),
            "text/plain": new Blob([markdown], { type: "text/plain" }),
          }),
        ]);
        dispatch(popupActions.showSuccess("Copied message"));
        handleClose();
      } else {
        fallbackCopy(markdown);
      }
    } catch (err) {
      console.error("Error copying to clipboard:", err);
    }
  };

  const fallbackCopy = (text) => {
    const textArea = document.createElement("textarea");
    textArea.value = text;
    document.body.appendChild(textArea);
    textArea.select();
    document.execCommand("copy");
    document.body.removeChild(textArea);
    handleClose();
  };

  const handleSetAsPrompt = () => {
    dispatch(chatActions.setPrompt(message.content));
    handleClose();
  };

  const handleCostDetails = () => {
    setDetailsOpen(true);
    handleClose();
  };

  const handleShare = async () => {
    try {
      setSharing(true);
      const shareRef = await addDoc(collection(db, "shares"), message);
      await navigator.clipboard.writeText(getShareUrl(shareRef.id));
      dispatch(popupActions.showSuccess("Share URL copied to clipboard"));
      handleClose();
    } catch (error) {
      console.log(error);
      dispatch(popupActions.showError("Couldn't share. Try again later"));
    } finally {
      setSharing(false);
    }
  };

  return (
    <div>
      <ConfirmModal
        open={deleting}
        setOpen={setDeleting}
        title="Confirm"
        message="Are you sure to delete this message?"
        confirmAction={handleDelete}
        cancelAction={handleClose}
      />
      <IconButton onClick={handleClick} color="text" disabled={processing}>
        {processing ? (
          <CircularProgress size={24} sx={{ color: colors.white }} />
        ) : (
          <MoreVertIcon />
        )}
      </IconButton>
      <Menu
        id="basic-menu"
        anchorEl={anchorEl}
        open={open}
        disabled={sharing}
        onClose={handleClose}
        MenuListProps={{
          "aria-labelledby": "basic-button",
        }}
      >
        <MenuItem onClick={copyMarkdownToClipboard}>
          <ListItemIcon>
            <ContentCopyIcon color="text" fontSize="small" />
          </ListItemIcon>
          <ListItemText color="text">Copy</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleCopy}>
          <ListItemIcon>
            <CopyAllIcon color="text" fontSize="small" />
          </ListItemIcon>
          <ListItemText color="text">Copy Markdown</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => setDeleting(true)}>
          <ListItemIcon>
            <DeleteIcon color="text" fontSize="small" />
          </ListItemIcon>
          <ListItemText color="text">Delete</ListItemText>
        </MenuItem>
      </Menu>
    </div>
  );
}
